/**
 * 移动端字段类型常量
 * 用于统一管理卡片区域、自定义字段前缀和系统字段名称
 */
/** 移动端卡片区域 */
export const CARD_SLOT = {
    /** 标题区域 */
    DEFAULT: 'default',
    /** 图片区域 */
    IMAGE: 'image',
    /** 内容区域 */
    CONTENT: 'content',
    /** 底部区域 */
    FOOTER: 'footer'
};
/**
 * 基础组件中被过滤的：
 * multitext: 富文本
 * attachment: 附件
 * 高级类组件被过滤的：
 * signature: 手写签名
 * graphicregn: 图文识别
 * subform: 子表单
 * 业务组件被过滤的：
 * car_model: 车型配置解析
 * query_platforms: 查询第三方平台
 */
/** 自定义字段前缀（格式：前缀_时间戳） */
export const FIELD_PREFIXES = {
    TEXT: 'text_', // 单行文本
    TEXTAREA: 'textarea_', // 多行文本
    PICKLIST: 'picklist_', // 下拉选项
    PHONE: 'phone_', // 电话
    EMAIL: 'email_', // 电子邮箱
    NUMBER: 'number_', // 数字
    DATETIME: 'datetime_', // 日期时间
    URL: 'url_', // 超链接
    LOCATION: 'location_', // 定位
    PICTURE: 'picture_', // 图片
    MULTI: 'multi_', // 复选框
    PERSONNEL: 'personnel_', // 人员
    DEPARTMENT: 'department_', // 部门
    MUTLIPICKLIST: 'mutlipicklist_', // 多级下拉
    FORMULA: 'formula_', // 简单公式
    AREA: 'area_', // 省市区
    IDENTIFIER: 'identifier_', // 自动编号
    REFERENCE: 'reference_', // 关联关系
    SENIORFORMULA: 'seniorformula_', // 高级公式
    FUNCTIONFORMULA: 'functionformula_', //函数公式
    BARCODE: 'barcode_', // 条码
    SERIALNUM: 'serialnum_', // 流水号
    CITEFORMULA: 'citeformula_' // 引用公式
};
/** 所有自定义字段前缀列表 */
export const FIELD_PREFIX_LIST = Object.values(FIELD_PREFIXES);
/** 系统字段名称（无时间戳后缀） */
export const SYSTEM_FIELDS = {
    /** 负责人 */
    PERSONNEL_PRINCIPAL: 'personnel_principal',
    /** 创建人 */
    PERSONNEL_CREATE_BY: 'personnel_create_by',
    /** 修改人 */
    PERSONNEL_MODIFY_BY: 'personnel_modify_by',
    /** 创建时间 */
    DATETIME_CREATE_TIME: 'datetime_create_time',
    /** 修改时间 */
    DATETIME_MODIFY_TIME: 'datetime_modify_time'
};
/** 所有系统字段名称集合 */
export const SYSTEM_FIELD_SET = new Set(Object.values(SYSTEM_FIELDS));
/** 下拉类字段前缀 */
export const PICKLIST_PREFIXES = [
    FIELD_PREFIXES.PICKLIST,
    FIELD_PREFIXES.MULTI,
    FIELD_PREFIXES.MUTLIPICKLIST
];
/**
 * 判断字段名是否匹配指定前缀
 */
export function hasFieldPrefix(fieldName, prefix) {
    if (!fieldName)
        return false;
    return fieldName.startsWith(prefix);
}
/**
 * 判断是否为自定义字段（前缀_时间戳）
 */
export function isCustomField(fieldName) {
    if (!fieldName)
        return false;
    return FIELD_PREFIX_LIST.some(prefix => {
        if (!fieldName.startsWith(prefix))
            return false;
        const suffix = fieldName.slice(prefix.length);
        return /^\d+$/.test(suffix);
    });
}
/**
 * 判断是否为系统字段
 */
export function isSystemField(fieldName) {
    return SYSTEM_FIELD_SET.has(fieldName);
}
/**
 * 判断是否为可拖拽字段（自定义字段或系统字段）
 */
export function isDraggableField(fieldName) {
    return isSystemField(fieldName) || isCustomField(fieldName);
}
/**
 * 判断是否为下拉类字段
 */
export function isPicklistField(fieldName) {
    if (!fieldName)
        return false;
    return PICKLIST_PREFIXES.some(prefix => fieldName.startsWith(prefix));
}
/**
 * 获取字段前缀的中文描述
 * @param prefix - 字段前缀
 * @returns 中文描述
 */
export function getFieldPrefixLabel(prefix) {
    const labels = {
        [FIELD_PREFIXES.TEXT]: '文本',
        [FIELD_PREFIXES.TEXTAREA]: '多行文本',
        [FIELD_PREFIXES.PICKLIST]: '下拉单选',
        [FIELD_PREFIXES.PHONE]: '电话',
        [FIELD_PREFIXES.EMAIL]: '邮箱',
        [FIELD_PREFIXES.NUMBER]: '数字',
        [FIELD_PREFIXES.DATETIME]: '日期时间',
        [FIELD_PREFIXES.URL]: '链接',
        [FIELD_PREFIXES.LOCATION]: '定位',
        [FIELD_PREFIXES.PICTURE]: '图片',
        [FIELD_PREFIXES.MULTI]: '下拉多选',
        [FIELD_PREFIXES.PERSONNEL]: '人员',
        [FIELD_PREFIXES.DEPARTMENT]: '部门',
        [FIELD_PREFIXES.MUTLIPICKLIST]: '多级下拉',
        [FIELD_PREFIXES.FORMULA]: '公式',
        [FIELD_PREFIXES.AREA]: '地区',
        [FIELD_PREFIXES.IDENTIFIER]: '标识',
        [FIELD_PREFIXES.REFERENCE]: '关联关系',
        [FIELD_PREFIXES.SENIORFORMULA]: '高级公式',
        [FIELD_PREFIXES.FUNCTIONFORMULA]: '函数公式',
        [FIELD_PREFIXES.BARCODE]: '条码',
        [FIELD_PREFIXES.SERIALNUM]: '流水号',
        [FIELD_PREFIXES.CITEFORMULA]: '引用公式'
    };
    return labels[prefix] || '未知';
}
/**
 * 获取系统字段的中文描述
 * @param field - 系统字段名
 * @returns 中文描述
 */
export function getSystemFieldLabel(field) {
    const labels = {
        [SYSTEM_FIELDS.PERSONNEL_PRINCIPAL]: '负责人',
        [SYSTEM_FIELDS.PERSONNEL_CREATE_BY]: '创建人',
        [SYSTEM_FIELDS.PERSONNEL_MODIFY_BY]: '修改人',
        [SYSTEM_FIELDS.DATETIME_CREATE_TIME]: '创建时间',
        [SYSTEM_FIELDS.DATETIME_MODIFY_TIME]: '修改时间'
    };
    return labels[field] || '未知';
}
export default FIELD_PREFIXES;
